import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import UpgradePrompt from '../components/game/UpgradePrompt';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';

const OPERATION_LIST = [
  { key: '+', labelKey: 'addition' },
  { key: '-', labelKey: 'subtraction' },
  { key: '\u00d7', labelKey: 'multiplication' },
  { key: '\u00f7', labelKey: 'division' },
];

const DURATIONS = [30, 45, 60, 90, 120, 180];

export default function SetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user, canAccessDifficulty } = useAuth();
  const subjectId = location.state?.subjectId || 'matematik';
  const subjectType = location.state?.subjectType || 'math';
  const isMCQ = subjectType === 'mcq';
  const classStudents = location.state?.classStudents || [];
  const className = location.state?.className || '';
  const hasClassRoster = classStudents.length > 0;

  const [team1Name, setTeam1Name] = useState(t('team1', language));
  const [team2Name, setTeam2Name] = useState(t('team2', language));
  const [difficulty, setDifficulty] = useState('easy');
  const [operations, setOperations] = useState(['+']);
  const [duration, setDuration] = useState(60);
  const [schoolName, setSchoolName] = useState(user?.school_name || '');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [soloMode, setSoloMode] = useState(false);
  const [yearLevel, setYearLevel] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);

  const YEAR_LEVELS = [
    { value: 'tahun-1', label: 'Tahun 1', diff: 'easy' },
    { value: 'tahun-2', label: 'Tahun 2', diff: 'easy' },
    { value: 'tahun-3', label: 'Tahun 3', diff: 'easy' },
    { value: 'tahun-4', label: 'Tahun 4', diff: 'medium' },
    { value: 'tahun-5', label: 'Tahun 5', diff: 'medium' },
    { value: 'tahun-6', label: 'Tahun 6', diff: 'medium' },
    { value: 'tingkatan-1', label: 'Tingkatan 1', diff: 'hard' },
    { value: 'tingkatan-2', label: 'Tingkatan 2', diff: 'hard' },
    { value: 'tingkatan-3', label: 'Tingkatan 3', diff: 'hard' },
  ];

  const toggleOperation = (op) => {
    setOperations(prev => {
      if (prev.includes(op)) {
        if (prev.length === 1) return prev; // must have at least one
        return prev.filter(o => o !== op);
      }
      return [...prev, op];
    });
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const startGame = async () => {
    const targetRoute = soloMode ? '/solo-game' : '/game';
    const gameState = {
      team1Name: soloMode ? (team1Name || user?.name || 'Player') : (team1Name || t('team1', language)),
      team2Name: soloMode ? 'AI Bot' : (team2Name || t('team2', language)),
      playerName: team1Name || user?.name || 'Player',
      difficulty,
      operations,
      duration,
      subjectId,
      subjectType,
      language,
      schoolName: schoolName || '',
      yearLevel,
    };

    // Try AI questions for MCQ subjects
    if (isMCQ && BACKEND_URL) {
      setLoadingAI(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/questions/game`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: subjectId, year: yearLevel, difficulty, count: 20 })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.questions && data.questions.length > 0) {
            gameState.aiQuestions = data.questions;
          }
        }
      } catch (e) {
        console.log('AI questions unavailable, using static bank');
      }
      setLoadingAI(false);
    }

    navigate(targetRoute, { state: gameState });
  };

  return (
    <div className="setup-page">
      <LanguageToggle className="setup-lang-toggle" />
      <UpgradePrompt show={showUpgrade} onClose={() => setShowUpgrade(false)}
        feature={upgradeFeature} requiredPlan="Basic" />

      <motion.div
        className="setup-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="setup-title">{t('gameSetup', language)}</h1>

        {/* Solo vs 2P Toggle */}
        <div className="setup-mode-toggle" data-testid="setup-mode-toggle">
          <button className={`setup-mode-btn ${!soloMode ? 'setup-mode-active' : ''}`} onClick={() => setSoloMode(false)}>
            {language === 'en' ? '2 Players' : '2 Pemain'}
          </button>
          <button className={`setup-mode-btn ${soloMode ? 'setup-mode-active setup-mode-ai' : ''}`} onClick={() => setSoloMode(true)}>
            {language === 'en' ? 'Solo vs AI' : 'Solo vs AI'} 🤖
          </button>
        </div>

        {/* Team Names */}
        {hasClassRoster && (
          <div className="setup-class-badge" data-testid="setup-class-badge">
            {language === 'en' ? `Class: ${className}` : `Kelas: ${className}`} ({classStudents.length} {language === 'en' ? 'students' : 'murid'})
          </div>
        )}
        <div className="setup-teams">
          <div className="setup-team-card setup-team1-card">
            <div className="setup-team-icon team1-icon">
              <svg viewBox="0 0 40 40" width="40" height="40">
                <circle cx="20" cy="14" r="10" fill="#FFCC99" />
                <rect x="8" y="24" width="24" height="14" rx="6" fill="#1a8cff" />
              </svg>
            </div>
            <label className="setup-label">{soloMode ? (language === 'en' ? 'Your Name' : 'Nama Anda') : t('team1', language)}</label>
            {hasClassRoster && !soloMode ? (
              <select className="setup-input setup-input-team1" value={team1Name}
                onChange={e => setTeam1Name(e.target.value)} data-testid="setup-team1-name-input">
                <option value="">{language === 'en' ? '-- Pick student --' : '-- Pilih murid --'}</option>
                {classStudents.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input className="setup-input setup-input-team1" type="text" value={team1Name}
                onChange={e => setTeam1Name(e.target.value)} placeholder={t('enterTeamName', language)}
                maxLength={15} data-testid="setup-team1-name-input" />
            )}
          </div>

          <div className="setup-vs">{t('vs', language)}</div>

          <div className="setup-team-card setup-team2-card">
            <div className="setup-team-icon team2-icon">
              <svg viewBox="0 0 40 40" width="40" height="40">
                <circle cx="20" cy="14" r="10" fill="#FFCC99" />
                <rect x="8" y="24" width="24" height="14" rx="6" fill="#ff8c1a" />
              </svg>
            </div>
            <label className="setup-label">{soloMode ? 'AI Bot 🤖' : t('team2', language)}</label>
            {soloMode ? (
              <div className="setup-ai-label">AI Bot</div>
            ) : hasClassRoster ? (
              <select className="setup-input setup-input-team2" value={team2Name}
                onChange={e => setTeam2Name(e.target.value)} data-testid="setup-team2-name-input">
                <option value="">{language === 'en' ? '-- Pick student --' : '-- Pilih murid --'}</option>
                {classStudents.filter(s => s !== team1Name).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input className="setup-input setup-input-team2" type="text" value={team2Name}
                onChange={e => setTeam2Name(e.target.value)} placeholder={t('enterTeamName', language)}
                maxLength={15} data-testid="setup-team2-name-input" />
            )}
          </div>
        </div>

        {/* School Name */}
        <div className="setup-section">
          <h3 className="setup-section-title">{t('schoolName', language)}</h3>
          <input
            className="setup-input setup-input-school"
            type="text"
            value={schoolName}
            onChange={e => setSchoolName(e.target.value)}
            placeholder={t('enterSchoolName', language)}
            maxLength={40}
            data-testid="setup-school-name-input"
          />
        </div>

        {/* Difficulty */}
        <div className="setup-section">
          <h3 className="setup-section-title">{t('difficulty', language)}</h3>
          <div className="setup-tabs" data-testid="setup-difficulty-tabs">
            <button
              className={`setup-tab ${difficulty === 'easy' ? 'setup-tab-active' : ''}`}
              onClick={() => setDifficulty('easy')}
              data-testid="setup-difficulty-easy"
            >
              {t('easy', language)}
              <span className="setup-tab-desc">{t('primaryLower', language)}</span>
            </button>
            <button
              className={`setup-tab ${difficulty === 'medium' ? 'setup-tab-active' : ''} ${user && !canAccessDifficulty('medium') ? 'setup-tab-locked' : ''}`}
              onClick={() => {
                if (user && !canAccessDifficulty('medium')) { setUpgradeFeature(t('medium', language)); setShowUpgrade(true); return; }
                setDifficulty('medium');
              }}
              data-testid="setup-difficulty-medium"
            >
              {user && !canAccessDifficulty('medium') && <Lock size={14} />}
              {t('medium', language)}
              <span className="setup-tab-desc">{t('primaryUpper', language)}</span>
            </button>
            <button
              className={`setup-tab ${difficulty === 'hard' ? 'setup-tab-active setup-tab-hard' : ''} ${user && !canAccessDifficulty('hard') ? 'setup-tab-locked' : ''}`}
              onClick={() => {
                if (user && !canAccessDifficulty('hard')) { setUpgradeFeature(t('hard', language)); setShowUpgrade(true); return; }
                setDifficulty('hard');
              }}
              data-testid="setup-difficulty-hard"
            >
              {user && !canAccessDifficulty('hard') && <Lock size={14} />}
              {t('hard', language)}
              <span className="setup-tab-desc">{t('secondary', language)}</span>
            </button>
          </div>
        </div>

        {/* Year Level Selector */}
        <div className="setup-section">
          <h3 className="setup-section-title">{language === 'en' ? 'School Year (AI Questions)' : 'Tahap Sekolah (Soalan AI)'}</h3>
          <select className="setup-input" value={yearLevel} onChange={e => setYearLevel(e.target.value)}
            data-testid="setup-year-level" style={{ fontSize: 15, padding: '10px 14px' }}>
            <option value="">{language === 'en' ? 'Auto (based on difficulty)' : 'Auto (ikut tahap)'}</option>
            {YEAR_LEVELS.map(y => (
              <option key={y.value} value={y.value}>{y.label}</option>
            ))}
          </select>
          <p style={{ fontSize: 12, color: 'hsl(var(--muted-fg))', marginTop: 4 }}>
            {language === 'en' ? 'AI generates questions matched to your school year (KSSR/KSSM)' : 'AI jana soalan selaras tahun sekolah anda (KSSR/KSSM)'}
          </p>
        </div>

        {/* Operations - only for Math */}
        {!isMCQ && (
        <div className="setup-section">
          <h3 className="setup-section-title">{t('operations', language)}</h3>
          <div className="setup-operations" data-testid="setup-operations-toggle-group">
            {OPERATION_LIST.map(op => (
              <button
                key={op.key}
                className={`setup-op-btn ${operations.includes(op.key) ? 'setup-op-active' : ''}`}
                onClick={() => toggleOperation(op.key)}
                data-testid={`setup-operation-${op.labelKey}`}
              >
                <span className="setup-op-symbol">{op.key}</span>
                <span className="setup-op-label">{t(op.labelKey, language)}</span>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Duration */}
        <div className="setup-section">
          <h3 className="setup-section-title">{t('duration', language)}</h3>
          <div className="setup-durations" data-testid="setup-duration-select">
            {DURATIONS.map(d => (
              <button
                key={d}
                className={`setup-duration-btn ${duration === d ? 'setup-duration-active' : ''}`}
                onClick={() => setDuration(d)}
                data-testid={`setup-duration-${d}`}
              >
                {d} {t('seconds', language)}
              </button>
            ))}
          </div>
        </div>

        {/* Start Button */}
        <motion.button
          className="setup-start-btn"
          onClick={startGame}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          data-testid="setup-start-game-button"
          disabled={loadingAI}
          style={loadingAI ? { opacity: 0.7 } : {}}
        >
          {loadingAI ? (language === 'en' ? 'Generating AI Questions...' : 'Menjana Soalan AI...') : t('startGame', language)}
        </motion.button>

        <button
          className="setup-back-btn"
          onClick={() => navigate('/')}
        >
          &larr; {t('backToHome', language)}
        </button>
      </motion.div>
    </div>
  );
}
